/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import DeleteAlert from "../components/DeleteAlert.jsx";
import MessageTable from "./marketing/components/MessageTable.jsx";
import MessageModal from "./marketing/components/MessageModal.jsx";
import MessageDetailDrawer from "./marketing/components/MessageDetailDrawer.jsx";
import { buttonClass } from "./marketing/styles";
import { useMarketingData } from "../hooks/useMarketingData";
import { PUSH_SEGMENTS } from "./dashboard/segments";
import { formatNumber } from "./dashboard/format";
import usePlanTier, { tierAllows } from "../hooks/usePlanTier";
import { initialsFrom } from "../utils/initials";
import { marketingSummary } from "./marketing/messageUtils";

function SummaryTile({ label, value }) {
  return (
    <div className="flex flex-col gap-1 rounded-[14px] border border-gray-200 bg-white px-5 py-4">
      <span className="text-[13px] text-gray-600">{label}</span>
      <span className="text-[26px] font-semibold tracking-tight text-gray-900 tabular-nums">
        {value}
      </span>
    </div>
  );
}

function EmptyMessages({ storeName, onCompose }) {
  const { t } = useLanguage();
  return (
    <section
      aria-labelledby="marketing-empty-title"
      className="flex flex-wrap items-center justify-center gap-10 rounded-[14px] border border-gray-200 bg-white px-6 py-14"
    >
      <div
        aria-hidden="true"
        className="flex w-[220px] flex-col items-center gap-[18px] rounded-[28px] bg-gray-800 px-3 pt-7 pb-6"
      >
        <span className="text-[40px] leading-none font-semibold tracking-tight text-white">
          09:41
        </span>
        <span className="flex w-full gap-2 rounded-[14px] bg-white/90 p-2.5 text-left">
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] bg-gray-900 text-[10px] font-bold text-white">
            {initialsFrom(storeName) || "L"}
          </span>
          <span className="text-xs leading-snug text-gray-900">
            <strong className="block">{storeName}</strong>
            {t("marketing.empty.sample")}
          </span>
        </span>
      </div>
      <div className="flex max-w-[420px] flex-col gap-3">
        <h2
          id="marketing-empty-title"
          className="m-0 text-[19px] font-semibold text-gray-900"
        >
          {t("marketing.empty.title")}
        </h2>
        <p className="m-0 text-sm leading-relaxed text-gray-700">
          {t("marketing.empty.body")}
        </p>
        {onCompose && (
          <button
            type="button"
            onClick={onCompose}
            className={`${buttonClass.primary} self-start`}
          >
            {t("marketing.empty.cta")}
          </button>
        )}
      </div>
    </section>
  );
}

const Marketing = () => {
  const { t, language } = useLanguage();
  const {
    messages,
    passes,
    integrations,
    formData,
    setFormData,
    isModalOpen,
    editingMessage,
    viewingMessage,
    viewMessage,
    deletingMessage,
    setDeletingMessage,
    isLoadingMessages,
    isLoadingIntegrations,
    isLoadingPhoneNumbers,
    isLoadingEmailAddresses,
    isSaving,
    isDeleting,
    phoneNumbers,
    emailAddresses,
    timeZone,
    timeZoneFromStore,
    handleOpenModal,
    handleCloseModal,
    handleSubmit,
    handleConfirmDelete,
    handleMessageTypeChange,
  } = useMarketingData();
  const { canManageStore, selectedStore } = useStore();
  const canTargetSegments = tierAllows(usePlanTier(selectedStore), "insights");
  const [searchParams, setSearchParams] = useSearchParams();
  const summary = useMemo(() => marketingSummary(messages), [messages]);
  // The dialog keeps the copy for the message it opened with while it fades out.
  const [deleteShown, setDeleteShown] = useState(null);
  if (deletingMessage && deletingMessage !== deleteShown)
    setDeleteShown(deletingMessage);
  const deleteStatus = (deletingMessage || deleteShown)?.status;

  // The dashboard's segment buttons land here as
  // /marketing?compose=push&segment=at_risk: open the push form with that
  // audience, once, then drop the params so a refresh or a close does not
  // reopen it.
  const composeSegment =
    searchParams.get("compose") === "push" &&
    PUSH_SEGMENTS.includes(searchParams.get("segment"))
      ? searchParams.get("segment")
      : null;
  useEffect(() => {
    if (!searchParams.has("compose")) return;
    if (composeSegment && canManageStore) {
      // A plan without segments still gets the push form, just without the
      // audience the API would refuse (403 plan_feature_locked).
      handleOpenModal(null, {
        message_type: "push",
        pass_id: "all",
        segment: canTargetSegments ? composeSegment : "",
      });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("compose");
        next.delete("segment");
        return next;
      },
      { replace: true },
    );
    // handleOpenModal is a fresh function every render; this must run once
    // per arrival, keyed on the params themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeSegment, canManageStore, canTargetSegments, searchParams]);

  // Delete is the one destructive action (M1). It is offered from the view
  // drawer too, which closes first: the confirm dialog sits below drawers.
  const askDelete = (message) => {
    viewMessage(null);
    setDeletingMessage(message);
  };

  if (isLoadingMessages) {
    return <div className="p-4">{t("common.loading")}</div>;
  }

  const compose = canManageStore ? () => handleOpenModal() : undefined;

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="m-0 text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
            {t("marketing.page.title")}
          </h1>
          <p className="m-0 text-[15px] text-gray-700">
            {t("marketing.page.subtitle")}
          </p>
        </div>
        {compose && (
          <button
            type="button"
            onClick={compose}
            className={buttonClass.primary}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("marketing.page.newMessage")}
          </button>
        )}
      </header>

      {messages.length === 0 ? (
        <EmptyMessages
          storeName={selectedStore?.name || ""}
          onCompose={compose}
        />
      ) : (
        <>
          <section
            aria-label={t("marketing.summary.label")}
            className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3"
          >
            <SummaryTile
              label={t("marketing.summary.sentLast30")}
              value={formatNumber(summary.sent, language)}
            />
            <SummaryTile
              label={t("marketing.summary.scheduled")}
              value={formatNumber(summary.scheduled, language)}
            />
          </section>

          <MessageTable
            messages={messages}
            timeZone={timeZone}
            onView={viewMessage}
            onEdit={canManageStore ? handleOpenModal : undefined}
            onDelete={canManageStore ? askDelete : undefined}
          />
        </>
      )}

      <MessageDetailDrawer
        message={viewingMessage}
        timeZone={timeZone}
        onClose={() => viewMessage(null)}
        onEdit={canManageStore ? handleOpenModal : undefined}
        onDelete={canManageStore ? askDelete : undefined}
      />

      <MessageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingMessage}
        isSaving={isSaving}
        passes={passes}
        integrations={integrations}
        isLoadingIntegrations={isLoadingIntegrations}
        phoneNumbers={phoneNumbers}
        emailAddresses={emailAddresses}
        isLoadingPhoneNumbers={isLoadingPhoneNumbers}
        isLoadingEmailAddresses={isLoadingEmailAddresses}
        handleMessageTypeChange={handleMessageTypeChange}
        timeZone={timeZone}
        timeZoneFromStore={timeZoneFromStore}
      />

      <DeleteAlert
        isOpen={!!deletingMessage}
        onClose={() => setDeletingMessage(null)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="marketing.delete.title"
        description={
          deleteStatus === "pending"
            ? "marketing.delete.pendingBody"
            : "marketing.delete.sentBody"
        }
        buttonText="marketing.delete.confirm"
      />
    </div>
  );
};

export default React.memo(Marketing);
